/**
 * CloudinaryViewer - Iframe-based Cloudinary Editor
 * Embeds external Cloudinary editor for image manipulation
 */

import { X, ChevronLeft, ChevronRight, Edit, Check, Loader2 } from "lucide-react";
import { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import { FavoriteButton } from './FavoriteButton';

interface CloudinaryViewerProps {
  imageUrl: string; // The exact URL from database to display
  baseUrl: string;
  assetPath: string;
  fullWidth: number;
  fullHeight: number;
  initialCrop?: { x: number; y: number; width: number; height: number };
  annotations?: Array<{ x: number; y: number; width: number; height: number }>;
  onClose?: () => void;
  onSave?: (cropUrl: string, crop: { x: number; y: number; width: number; height: number }, annotations?: Array<{ x: number; y: number; width: number; height: number }>) => void;
  currentIndex?: number;
  totalAssets?: number;
  onPrevious?: () => void;
  onNext?: () => void;
  initialHeader?: string;
  initialSubheader?: string;
  onSaveMetadata?: (header: string, subheader: string) => Promise<void>;
  assetId?: string;
  isFavorited?: boolean;
}

export default function CloudinaryViewer({
  imageUrl,
  baseUrl,
  assetPath,
  fullWidth,
  fullHeight,
  initialCrop,
  annotations = [],
  onClose,
  onSave,
  currentIndex = 0,
  totalAssets = 1,
  onPrevious,
  onNext,
  initialHeader = "",
  initialSubheader = "",
  onSaveMetadata,
  assetId,
  isFavorited = false,
}: CloudinaryViewerProps) {
  const [iframeUrl, setIframeUrl] = useState<string>("");
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [header, setHeader] = useState(initialHeader);
  const [subheader, setSubheader] = useState(initialSubheader);
  const [isEditingHeader, setIsEditingHeader] = useState(false);
  const [savingMetadata, setSavingMetadata] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [pendingCrop, setPendingCrop] = useState<{ url: string; crop: any; annotations?: any[] } | null>(null);
  // Track the current crop - starts with initialCrop but updates after save
  const [currentCrop, setCurrentCrop] = useState(initialCrop);
  // Track current annotations - updates when user edits in the editor
  const [currentAnnotations, setCurrentAnnotations] = useState(annotations);
  // Track last navigation time for debouncing
  const lastNavigationRef = useRef<number>(0);
  const navigationDebounceMs = 300; // Prevent navigation faster than 300ms

  const hasPrevious = currentIndex > 0;
  const hasNext = currentIndex < totalAssets - 1;

  // Reset everything when navigating to a different asset
  useEffect(() => {
    setHeader(initialHeader);
    setSubheader(initialSubheader);
    setIsEditingHeader(false);
    setCurrentCrop(initialCrop);
    setCurrentAnnotations(annotations);
    setIsDirty(false);
    setPendingCrop(null);
  }, [assetId]);

  // Update values when props change, but don't reset edit mode
  useEffect(() => {
    if (!isEditingHeader) {
      setHeader(initialHeader);
      setSubheader(initialSubheader);
    }
  }, [initialHeader, initialSubheader, isEditingHeader]);

  // Update crop and annotations when they change from parent
  useEffect(() => {
    setCurrentCrop(initialCrop);
    setCurrentAnnotations(annotations);
  }, [initialCrop, annotations]);

  useEffect(() => {
    // Use the exact image URL from database - don't rebuild it!
    // This is the same URL shown on the card
    console.log('CloudinaryViewer: Using imageUrl from database:', imageUrl);
    const encodedImageUrl = encodeURIComponent(imageUrl);
    // Update iframe URL - the iframe will reload when src changes
    const zoomUrl = `https://cloudinary-canvas-studio.lovable.app/zoom?url=${encodedImageUrl}`;
    console.log('CloudinaryViewer: Zoom URL:', zoomUrl);

    // Update iframe src directly if iframe is already loaded
    if (iframeRef.current && iframeRef.current.src !== zoomUrl) {
      // Clear the current iframe content before loading new URL
      iframeRef.current.src = 'about:blank';
      // Small delay to ensure cleanup, then load new content
      setTimeout(() => {
        if (iframeRef.current) {
          iframeRef.current.src = zoomUrl;
        }
      }, 50);
    }

    setIframeUrl(zoomUrl);
  }, [imageUrl]);

  // Cleanup iframe on unmount to prevent memory leaks
  useEffect(() => {
    return () => {
      // Aggressively clear iframe to help browser garbage collect
      if (iframeRef.current) {
        // Remove event listeners that the iframe might have added
        iframeRef.current.onload = null;
        // Clear src to release memory
        iframeRef.current.src = 'about:blank';
        // Force a small delay then try to trigger garbage collection hint
        setTimeout(() => {
          if (iframeRef.current) {
            try {
              // Try to access contentWindow to trigger cleanup
              iframeRef.current.contentWindow?.stop();
            } catch (e) {
              // Ignore cross-origin errors
            }
          }
        }, 0);
      }
    };
  }, []);

  // Listen for messages from iframe
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // Only accept messages from the editor origin
      if (!event.origin.includes('cloudinary-canvas-studio') && !event.origin.includes('lovable.app')) {
        return;
      }

      // New unified stateChanged event with isDirty flag from iframe
      if (event.data.type === 'stateChanged') {
        const { url, crop, annotations, isDirty: remoteIsDirty } = event.data.payload;

        // Use the isDirty flag from the iframe itself
        setIsDirty(remoteIsDirty);

        // Store the pending crop data with annotations if dirty
        if (remoteIsDirty) {
          setPendingCrop({ url, crop, annotations });
        } else {
          setPendingCrop(null);
        }
      }

      if (event.data.type === 'close-editor') {
        if (isDirty) {
          const confirmClose = window.confirm('You have unsaved changes. Are you sure you want to close?');
          if (!confirmClose) return;
        }
        onClose?.();
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [onSave, onClose, initialCrop, fullWidth, fullHeight, isDirty]);

  // Debounced navigation helper
  const canNavigate = () => {
    const now = Date.now();
    if (now - lastNavigationRef.current < navigationDebounceMs) {
      return false;
    }
    lastNavigationRef.current = now;
    return true;
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Disable arrow key navigation when editing header/subheader
      if (isEditingHeader && (e.key === 'ArrowLeft' || e.key === 'ArrowRight')) {
        return; // Let the input field handle arrow keys
      }

      if (e.key === 'ArrowLeft' && hasPrevious && onPrevious) {
        if (!canNavigate()) return;
        if (isDirty) {
          const confirmNavigate = window.confirm('You have unsaved changes. Discard and navigate to previous?');
          if (!confirmNavigate) return;
          setIsDirty(false);
          setPendingCrop(null);
        }
        e.preventDefault();
        onPrevious();
      } else if (e.key === 'ArrowRight' && hasNext && onNext) {
        if (!canNavigate()) return;
        if (isDirty) {
          const confirmNavigate = window.confirm('You have unsaved changes. Discard and navigate to next?');
          if (!confirmNavigate) return;
          setIsDirty(false);
          setPendingCrop(null);
        }
        e.preventDefault();
        onNext();
      } else if (e.key === 'Escape' && onClose) {
        // Don't close if editing - let user cancel edit with Escape first
        if (isEditingHeader) {
          return; // Let cancel button handle escape
        }
        if (isDirty) {
          const confirmClose = window.confirm('You have unsaved changes. Discard and close?');
          if (!confirmClose) return;
        }
        e.preventDefault();
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [hasPrevious, hasNext, onPrevious, onNext, onClose, isDirty, isEditingHeader]);

  // Note: We rely on 'editor-ready' postMessage instead of iframe onLoad
  // because the editor needs time to initialize after the iframe loads

  const handleSaveClick = async () => {
    if (!pendingCrop && !isDirty) {
      toast.info('No changes to save');
      return;
    }

    try {
      let cropUrl;
      let crop;
      let annotations;

      // Use the URL directly from the iframe control - it already has all transformations
      if (pendingCrop) {
        cropUrl = pendingCrop.url;
        crop = pendingCrop.crop;
        annotations = pendingCrop.annotations;
      } else {
        // Fallback: build from current state
        const parts = [];
        if (currentCrop) {
          parts.push(
            `c_crop,x_${Math.round(currentCrop.x)},y_${Math.round(currentCrop.y)},w_${Math.round(currentCrop.width)},h_${Math.round(currentCrop.height)}`
          );
        }
        parts.push('q_auto,f_auto');
        cropUrl = `${baseUrl}/${parts.join("/")}/${assetPath}`;
        crop = currentCrop || { x: 0, y: 0, width: fullWidth, height: fullHeight };
        annotations = currentAnnotations;
      }

      // Save to database
      await onSave?.(cropUrl, crop, annotations);

      // Clear dirty state after successful save
      setIsDirty(false);
      setPendingCrop(null);

      // The parent will update initialCrop and annotations props after fetching from DB
      // which will trigger the useEffect to reload the iframe with fresh data
      toast.success('Changes saved successfully');
    } catch (error) {
      console.error('Error saving changes:', error);
      toast.error('Failed to save changes');
    }
  };

  const handleCancelClick = () => {
    if (!isDirty) return;

    const confirmCancel = window.confirm('Discard unsaved changes?');
    if (confirmCancel) {
      setIsDirty(false);
      setPendingCrop(null);
      // Force iframe to reload with the current saved crop (not pending changes)
      // The iframe URL is already built from currentCrop, so we just need to force a reload
      setLoading(true);
      // Trigger useEffect by creating new object reference
      if (currentCrop) {
        setCurrentCrop({ ...currentCrop });
      } else {
        // If no crop, just force iframe reload
        setIframeUrl(prev => prev.split('&reset=')[0] + '&reset=' + Date.now());
      }
      toast.info('Changes discarded');
    }
  };

  const handleSaveMetadata = async () => {
    if (!onSaveMetadata) return;

    setSavingMetadata(true);
    try {
      await onSaveMetadata(header, subheader);
      setIsEditingHeader(false);
      toast.success('Metadata saved successfully');
    } catch (error) {
      console.error('Error saving metadata:', error);
      toast.error('Failed to save metadata');
    } finally {
      setSavingMetadata(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black flex flex-col">
      {/* Header */}
      <div className="bg-black/90 border-b border-white/10">
        <div className="flex items-center justify-between p-4">
          <div className="flex items-center gap-4 flex-1">
            <div className="flex flex-col gap-1 flex-1">
              {!isEditingHeader ? (
                <div className="flex items-center gap-2">
                  {assetId && (
                    <FavoriteButton
                      assetId={assetId}
                      isFavorited={isFavorited}
                      variant="icon"
                      className="hover:bg-white/10 !text-white"
                    />
                  )}
                  <div className="flex flex-col">
                    {header && (
                      <div className="text-white text-sm font-medium">{header}</div>
                    )}
                    {subheader && (
                      <div className="text-white/60 text-xs">{subheader}</div>
                    )}
                  </div>
                  {onSaveMetadata && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setIsEditingHeader(true);
                      }}
                      className="p-1 rounded hover:bg-white/10 transition-colors"
                      title="Edit header and subheader"
                    >
                      <Edit className="h-3.5 w-3.5 text-white/70" />
                    </button>
                  )}
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <div className="flex flex-col gap-1 flex-1">
                    <input
                      type="text"
                      value={header}
                      onChange={(e) => setHeader(e.target.value)}
                      placeholder="Header"
                      className="px-2 py-1 text-sm bg-white/10 border border-white/20 rounded text-white placeholder:text-white/40 focus:outline-none focus:ring-1 focus:ring-white/40"
                    />
                    <input
                      type="text"
                      value={subheader}
                      onChange={(e) => setSubheader(e.target.value)}
                      placeholder="Subheader"
                      className="px-2 py-1 text-xs bg-white/10 border border-white/20 rounded text-white placeholder:text-white/40 focus:outline-none focus:ring-1 focus:ring-white/40"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSaveMetadata();
                    }}
                    disabled={savingMetadata}
                    className="p-1 rounded bg-blue-600 hover:bg-blue-700 transition-colors disabled:opacity-50"
                    title="Save metadata"
                  >
                    {savingMetadata ? (
                      <Loader2 className="h-3.5 w-3.5 text-white animate-spin" />
                    ) : (
                      <Check className="h-3.5 w-3.5 text-white" />
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setHeader(initialHeader);
                      setSubheader(initialSubheader);
                      setIsEditingHeader(false);
                    }}
                    className="p-1 rounded hover:bg-white/10 transition-colors"
                    title="Cancel"
                  >
                    <X className="h-3.5 w-3.5 text-white/70" />
                  </button>
                </div>
              )}
            </div>
            {totalAssets > 1 && (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => {
                    if (!canNavigate()) return;
                    if (isDirty) {
                      const confirmNavigate = window.confirm('You have unsaved changes. Discard and navigate to previous?');
                      if (!confirmNavigate) return;
                      setIsDirty(false);
                      setPendingCrop(null);
                    }
                    onPrevious?.();
                  }}
                  disabled={!hasPrevious}
                  className={`p-2 rounded-lg transition-colors ${
                    hasPrevious
                      ? 'hover:bg-white/10 text-white'
                      : 'text-white/30 cursor-not-allowed'
                  }`}
                  title="Previous (←)"
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>
                <span className="text-white/70 text-sm">
                  {currentIndex + 1} / {totalAssets}
                </span>
                <button
                  onClick={() => {
                    if (!canNavigate()) return;
                    if (isDirty) {
                      const confirmNavigate = window.confirm('You have unsaved changes. Discard and navigate to next?');
                      if (!confirmNavigate) return;
                      setIsDirty(false);
                      setPendingCrop(null);
                    }
                    onNext?.();
                  }}
                  disabled={!hasNext}
                  className={`p-2 rounded-lg transition-colors ${
                    hasNext
                      ? 'hover:bg-white/10 text-white'
                      : 'text-white/30 cursor-not-allowed'
                  }`}
                  title="Next (→)"
                >
                  <ChevronRight className="h-5 w-5" />
                </button>
              </div>
            )}
          </div>
          <button
            onClick={() => {
              if (isDirty) {
                const confirmClose = window.confirm('You have unsaved changes. Discard and close?');
                if (!confirmClose) return;
              }
              onClose?.();
            }}
            className="p-2 rounded-lg hover:bg-white/10 transition-colors"
            title="Close (ESC)"
          >
            <X className="h-5 w-5 text-white" />
          </button>
        </div>
      </div>

      {/* Zoom widget iframe */}
      <div className="flex-1 relative">
        <iframe
          id="zoom-widget"
          ref={iframeRef}
          src={iframeUrl}
          className="w-full h-full border-0"
          title="Cloudinary Zoom Widget"
          allow="clipboard-write"
        />

        {/* Side navigation buttons */}
        {totalAssets > 1 && (
          <>
            {hasPrevious && (
              <button
                onClick={() => {
                  if (!canNavigate()) return;
                  if (isDirty) {
                    const confirmNavigate = window.confirm('You have unsaved changes. Discard and navigate to previous?');
                    if (!confirmNavigate) return;
                    setIsDirty(false);
                    setPendingCrop(null);
                  }
                  onPrevious?.();
                }}
                className="absolute left-4 top-1/2 -translate-y-1/2 p-3 rounded-full bg-black/50 hover:bg-black/70 text-white transition-all backdrop-blur-sm"
                title="Previous (←)"
              >
                <ChevronLeft className="h-6 w-6" />
              </button>
            )}
            {hasNext && (
              <button
                onClick={() => {
                  if (!canNavigate()) return;
                  if (isDirty) {
                    const confirmNavigate = window.confirm('You have unsaved changes. Discard and navigate to next?');
                    if (!confirmNavigate) return;
                    setIsDirty(false);
                    setPendingCrop(null);
                  }
                  onNext?.();
                }}
                className="absolute right-4 top-1/2 -translate-y-1/2 p-3 rounded-full bg-black/50 hover:bg-black/70 text-white transition-all backdrop-blur-sm"
                title="Next (→)"
              >
                <ChevronRight className="h-6 w-6" />
              </button>
            )}
          </>
        )}
      </div>

      {/* Footer with actions */}
      <div className="flex items-center justify-between p-4 bg-black/90 border-t border-white/10">
        <div className="text-white/50 text-sm">
          {isDirty ? (
            <span className="text-yellow-400">⚠ Unsaved changes</span>
          ) : totalAssets > 1 ? (
            <>Use arrow keys (← →) to navigate • ESC to close</>
          ) : (
            <>Use the editor above to crop and edit your image</>
          )}
        </div>
        <div className="flex items-center gap-2">
          {!isDirty ? (
            <button
              onClick={() => {
                if (isDirty) {
                  const confirmClose = window.confirm('You have unsaved changes. Discard and close?');
                  if (!confirmClose) return;
                }
                onClose?.();
              }}
              className="px-4 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-white transition-colors"
            >
              Close
            </button>
          ) : (
            <>
              <button
                onClick={handleCancelClick}
                className="px-4 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-white transition-colors"
              >
                Cancel
              </button>
              {onSave && (
                <button
                  onClick={handleSaveClick}
                  className="px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white transition-colors"
                >
                  Save Changes
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
